import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Activity, 
  Calendar,
  Filter,
  Download,
  BarChart3,
  PieChart,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AccuracyStats {
  totalPredictions: number;
  wonPredictions: number;
  lostPredictions: number;
  pendingPredictions: number;
  hitRate: number;
  avgConfidence: number;
  drawVolatility: number;
  modelDrift: number;
}

interface PredictionBreakdown {
  sport: string;
  total: number;
  won: number;
  lost: number;
  hitRate: number;
  avgOdds: number;
}

export function PredictionAccuracyDashboard() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AccuracyStats | null>(null);
  const [breakdown, setBreakdown] = useState<PredictionBreakdown[]>([]);
  const [timeFilter, setTimeFilter] = useState('7d');
  const [sportFilter, setSportFilter] = useState('all');
  const [predictionType, setPredictionType] = useState('all');

  useEffect(() => {
    fetchAccuracyData();
  }, [timeFilter, sportFilter, predictionType]);

  const fetchAccuracyData = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const now = new Date();
      const daysAgo = timeFilter === '7d' ? 7 : timeFilter === '30d' ? 30 : 90;
      const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

      // Build query
      let query = supabase
        .from('enhanced_tips')
        .select('*')
        .gte('created_at', startDate.toISOString());

      if (sportFilter !== 'all') {
        query = query.eq('sport', sportFilter);
      }

      if (predictionType === 'ai') {
        query = query.eq('is_ai_generated', true);
      } else if (predictionType === 'human') {
        query = query.eq('is_ai_generated', false);
      }

      const { data: tips, error } = await query;

      if (error) throw error;

      // Calculate statistics
      const won = tips?.filter(t => t.status === 'won').length || 0;
      const lost = tips?.filter(t => t.status === 'lost').length || 0;
      const pending = tips?.filter(t => t.status === 'pending').length || 0;
      const total = tips?.length || 1;

      const avgConfidence = tips?.reduce((acc, t) => acc + (t.confidence_level || 0), 0) / total;
      
      // Calculate draw volatility (variation in confidence scores)
      const confidenceValues = tips?.map(t => t.confidence_level || 0) || [];
      const meanConfidence = confidenceValues.reduce((a, b) => a + b, 0) / confidenceValues.length;
      const variance = confidenceValues.reduce((acc, val) => acc + Math.pow(val - meanConfidence, 2), 0) / confidenceValues.length;
      const drawVolatility = Math.sqrt(variance);

      // Calculate model drift (change in accuracy over time)
      const firstHalf = tips?.slice(0, Math.floor(total / 2)) || [];
      const secondHalf = tips?.slice(Math.floor(total / 2)) || [];
      const firstHalfAccuracy = firstHalf.filter(t => t.status === 'won').length / (firstHalf.length || 1);
      const secondHalfAccuracy = secondHalf.filter(t => t.status === 'won').length / (secondHalf.length || 1);
      const modelDrift = ((secondHalfAccuracy - firstHalfAccuracy) * 100);

      setStats({
        totalPredictions: total,
        wonPredictions: won,
        lostPredictions: lost,
        pendingPredictions: pending,
        hitRate: (won / (won + lost || 1)) * 100,
        avgConfidence: avgConfidence,
        drawVolatility: drawVolatility,
        modelDrift: modelDrift
      });

      // Calculate breakdown by sport
      const sportGroups: { [key: string]: typeof tips } = {};
      tips?.forEach(tip => {
        if (!sportGroups[tip.sport]) {
          sportGroups[tip.sport] = [];
        }
        sportGroups[tip.sport].push(tip);
      });

      const breakdownData = Object.entries(sportGroups).map(([sport, sportTips]) => {
        const sportWon = sportTips.filter(t => t.status === 'won').length;
        const sportLost = sportTips.filter(t => t.status === 'lost').length;
        const avgOdds = sportTips.reduce((acc, t) => acc + (t.odds || 0), 0) / sportTips.length;

        return {
          sport,
          total: sportTips.length,
          won: sportWon,
          lost: sportLost,
          hitRate: (sportWon / (sportWon + sportLost || 1)) * 100,
          avgOdds
        };
      });

      setBreakdown(breakdownData);
    } catch (error: any) {
      toast({
        title: 'Error fetching accuracy data',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    const data = {
      stats,
      breakdown,
      filters: { timeFilter, sportFilter, predictionType },
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accuracy-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Report Exported',
      description: 'Accuracy report downloaded successfully'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Prediction Accuracy Dashboard</h2>
          <p className="text-muted-foreground">Track model performance and prediction quality</p>
        </div>
        <Button onClick={exportData} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                <SelectItem value="Football">Football</SelectItem>
                <SelectItem value="Basketball">Basketball</SelectItem>
                <SelectItem value="Tennis">Tennis</SelectItem>
              </SelectContent>
            </Select>

            <Select value={predictionType} onValueChange={setPredictionType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="ai">AI Generated</SelectItem>
                <SelectItem value="human">Human Tipsters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Hit Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-foreground">
                {stats?.hitRate.toFixed(1)}%
              </div>
              <Target className="h-8 w-8 text-profit" />
            </div>
            <Progress value={stats?.hitRate} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {stats?.wonPredictions} won / {stats?.totalPredictions} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Confidence</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-foreground">
                {stats?.avgConfidence.toFixed(1)}%
              </div>
              <Activity className="h-8 w-8 text-primary" />
            </div>
            <Progress value={stats?.avgConfidence} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Model certainty level
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Draw Volatility</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-foreground">
                {stats?.drawVolatility.toFixed(1)}
              </div>
              <BarChart3 className="h-8 w-8 text-premium" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Confidence variance indicator
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Model Drift</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className={`text-2xl font-bold ${stats?.modelDrift && stats.modelDrift > 0 ? 'text-profit' : 'text-destructive'}`}>
                {stats?.modelDrift && stats.modelDrift > 0 ? '+' : ''}{stats?.modelDrift.toFixed(1)}%
              </div>
              {stats?.modelDrift && stats.modelDrift > 0 ? (
                <TrendingUp className="h-8 w-8 text-profit" />
              ) : (
                <TrendingDown className="h-8 w-8 text-destructive" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Accuracy trend over time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by Sport */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Sport</CardTitle>
          <CardDescription>Detailed breakdown of prediction accuracy across different sports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {breakdown.map((sport) => (
              <div key={sport.sport} className="border-b border-border pb-4 last:border-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{sport.sport}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {sport.total} predictions
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground">
                      {sport.hitRate.toFixed(1)}% Hit Rate
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Avg @{sport.avgOdds.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-profit/10 rounded">
                    <div className="text-sm font-bold text-profit">{sport.won}</div>
                    <div className="text-xs text-muted-foreground">Won</div>
                  </div>
                  <div className="text-center p-2 bg-destructive/10 rounded">
                    <div className="text-sm font-bold text-destructive">{sport.lost}</div>
                    <div className="text-xs text-muted-foreground">Lost</div>
                  </div>
                  <div className="text-center p-2 bg-secondary/10 rounded">
                    <div className="text-sm font-bold text-foreground">{sport.total - sport.won - sport.lost}</div>
                    <div className="text-xs text-muted-foreground">Pending</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alert for Low Accuracy */}
      {stats && stats.hitRate < 50 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <h3 className="font-semibold text-destructive mb-1 text-base">Low Accuracy Alert</h3>
              <p className="text-sm text-muted-foreground">
                Current hit rate is below 50%. Consider reviewing prediction models and recalibrating confidence thresholds.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
